import { GeoJSONSource, Map as MaplibreMap, Marker } from 'maplibre-gl';
import { FeMapPlugin } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { assert } from '@fe/core';
import { Position } from 'geojson';
import { UnitFormatter } from '../../../../formatters/unit-formatter';

const SOURCE = 'fe-dist-bearing-line-source';
const LINE = 'fe-dist-bearing-line-layer';
const LINE_COLOR = '#ed6498';
const LINE_WIDTH = 2;

export class DistanceBearingPlugin implements FeMapPlugin {
    private map: MaplibreMap | null = null;
    private id: number = 0;
    private lastPoint: Position | null = null;
    private justAdded: boolean = false;
    private markers: Marker[] = [];

    set currentPoint(point: Position) {
        if (!this.map || !this.lastPoint) {
            return;
        }

        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        const segment = [this.lastPoint, point];
        if (this.justAdded) {
            // Add feature following the cursor. Only once. Do not update id
            source?.updateData({
                add: [turf.lineString(segment, null, { id: this.id })]
            });
            this.justAdded = false;
        } else {
            // Feature following the cursor has been added. Update it
            source?.updateData({
                update: [
                    {
                        id: this.id,
                        newGeometry: {
                            type: 'LineString',
                            coordinates: segment
                        } as GeoJSON.Geometry
                    }
                ]
            });
        }
    }

    set show(value: boolean) {
        if (!this.map) {
            return;
        }
        this.map.setLayoutProperty(LINE, 'visibility', value ? 'visible' : 'none');
    }

    onAdd(map: MaplibreMap): void {
        assert(!this.map);
        this.map = map;

        this.map.addSource(SOURCE, {
            type: 'geojson',
            data: turf.featureCollection([])
        });

        this.map.addLayer({
            id: LINE,
            type: 'line',
            source: SOURCE,
            layout: {
                'line-cap': 'round',
                'line-join': 'round'
            },
            paint: {
                'line-color': LINE_COLOR,
                'line-width': LINE_WIDTH
            }
        });
    }

    onRemove(): void {
        assert(this.map);

        this.map.removeLayer(LINE);
        this.map.removeSource(SOURCE);
        this.map = null;
    }

    addPoint(point: Position): void {
        if (!this.map) {
            return;
        }

        if (!this.lastPoint) {
            this.lastPoint = point;
            this.justAdded = true;
            return;
        }

        const feature = turf.lineString([this.lastPoint, point], null, { id: this.id++ });
        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({
            remove: this.justAdded ? undefined : [this.id], // Remove the last feature if it was added by moving the cursor
            add: [feature]
        });

        this.lastPoint = point;
        this.justAdded = true;
    }

    clear(): void {
        if (!this.map) {
            return;
        }

        this.clearMarkers();
        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({
            removeAll: true
        });

        this.id = 0;
        this.lastPoint = null;
        this.justAdded = false;
    }

    addMarker(position: Position, item: { distance: number; bearing: number; total: number }): void {
        if (!this.map) {
            return;
        }

        const markerElement = document.createElement('div');
        markerElement.className = 'pe-none k-card p-1';
        markerElement.style.position = 'absolute';
        markerElement.style.zIndex = '888';
        markerElement.style.fontSize = '0.7rem';
        markerElement.style.width = 'max-content';
        markerElement.style.whiteSpace = 'nowrap';
        markerElement.textContent = `${UnitFormatter.formatDistance(item.distance)}, ${item.bearing} deg, total: ${UnitFormatter.formatDistance(item.total)}`;
        const marker = new Marker({
            element: markerElement,
            anchor: 'top-left',
            offset: [5, 5]
        })
            .setLngLat(position as [number, number])
            .addTo(this.map);
        this.markers.push(marker);
    }

    private clearMarkers(): void {
        for (const marker of this.markers) {
            marker.remove();
        }
        this.markers = [];
    }
}
